import { useEffect, useRef } from "react";

/// Mount order of the open dismissible overlays. Escape closes only the last
/// entry, so an overlay opened on top of another peels off one layer instead of
/// both. ONE stack for dialogs AND panels — a second stack would let a single
/// Escape close a dialog and the panel underneath it at the same time.
const openOverlays: object[] = [];

/// Close-on-Escape for the topmost overlay. Passing no handler means the
/// overlay is deliberately non-dismissible, so it stays out of the stack rather
/// than sitting on top and swallowing Escape from whatever is beneath it.
export function useEscapeStack(open: boolean, onEscape?: () => void): void {
  // Read through a ref so the effect depends on identity-stable values only —
  // re-running it on a new handler identity would re-push this overlay above an
  // already-open child and steal its Escape.
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;
  const armed = open && !!onEscape;

  useEffect(() => {
    if (!armed) return;
    const token = {};
    openOverlays.push(token);
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if (openOverlays[openOverlays.length - 1] !== token) return;
      onEscapeRef.current?.();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      const i = openOverlays.indexOf(token);
      if (i !== -1) openOverlays.splice(i, 1);
    };
  }, [armed]);
}
